import { ARENA_H, ARENA_W } from "../config.ts";
import { LevelBuilder } from "./builder.ts";
import { EN_BEAST, EN_BOMBER, EN_HEN, EN_LASER, EN_NINJA } from "../entities/enemy.ts";
import { gapWall, sweepBar } from "./patterns.ts";
import type { Level } from "./levelTypes.ts";

/**
 * LEVEL 4 — "FLUX"
 * The challenge version of Level 1's pillar gauntlet, with the heavyweights
 * on top. Five movements:
 *
 *   1. PILLARS    — the flying pink walls, turned up from Level 1.
 *   2. SPRINKLE   — the heavyweights introduce themselves one at a time:
 *                   bomber, beast, hen, ninja, each showing its power.
 *   3. AMBUSH     — four eyeballs post up in the four corners, half a second
 *                   apart so their beams never sync, then slide off toward
 *                   the sides when their time is up. While they hold the
 *                   corners: two hens, then two ninjas, then two beasts at
 *                   the very end — the beasts charge straight into the
 *                   wall rush.
 *   4. WALL RUSH  — the pink walls return in force alongside charging
 *                   beasts and a ninja.
 *   5. FINALE     — everything at once: two bombers, two beasts, a laser
 *                   eye dead center, hens, a ninja, and pillars flying.
 *
 * No meteors here — they sit this one out and return for the dragon.
 */
export function buildLevel4(): Level {
  const b = new LevelBuilder(4, "FLUX", "Gauntlet", 104);

  // --- 1. PILLARS (2-14s): the pillar gauntlet, turned up from Level 1. All
  // the opening pillars run at the MIDDLE speed tier — a steady, readable
  // pace before the mix starts varying later in the level. ---
  b.every(2, 13, 1.4, (ctx, t) => {
    const k = Math.round((t - 2) / 1.4);
    const axis = k % 2 === 0 ? "h" : "v";
    const pos = 0.12 + ((k * 0.31) % 0.76);
    sweepBar(ctx, axis, pos, 1, k % 3 !== 0); // middle tier throughout the intro
  });

  // --- 2. SPRINKLE (15-37s): the heavyweights introduce themselves ---
  b.cue(16, "intensity:1");
  // First bomber on a nearly-clear board to learn the satchel.
  b.at(16, (ctx) => {
    ctx.enemy(EN_BOMBER, -40, ARENA_H * 0.5, { homeX: ARENA_W * 0.28 });
  });
  // One lone slow pillar late in the window keeps the feet moving, nothing more.
  b.at(22, (ctx) => sweepBar(ctx, "h", 0.7, 0, true));
  // The first charger, introduced alone, from the LEFT.
  b.at(25, (ctx) => {
    ctx.enemy(EN_BEAST, -16, ARENA_H * 0.5, { phase: 0, life: 9 });
  });
  // First hen hops in once the opening satchel is spent.
  b.at(27, (ctx) => {
    ctx.enemy(EN_HEN, ARENA_W + 30, ARENA_H * 0.78, {});
  });
  // First ninja: a lone dasher through the pillar traffic.
  b.at(33, (ctx) => {
    ctx.enemy(EN_NINJA, ARENA_W * 0.5, ARENA_H * 0.35, {});
  });
  b.every(27, 37, 3.2, (ctx, t) => {
    const k = Math.round((t - 27) / 3.2);
    sweepBar(ctx, k % 2 === 0 ? "v" : "h", 0.2 + (k % 3) * 0.3, k % 2, k % 2 === 0);
  });

  // --- 3. CORNER AMBUSH (38-53s): four eyeballs post up in the four
  // corners, staggered a half-second apart so their laser windups fire at
  // different times, and slide off SIDEWAYS when their watch ends. While
  // they pin the corners, each heavyweight makes one guest appearance. ---
  b.cue(38, "intensity:2");
  b.at(38, (ctx) => {
    ctx.enemy(EN_LASER, -40, ARENA_H * 0.14, {
      homeX: ARENA_W * 0.14,
      homeY: ARENA_H * 0.14,
      life: 12,
      laserExitSide: true,
    });
  });
  b.at(38.5, (ctx) => {
    ctx.enemy(EN_LASER, ARENA_W + 40, ARENA_H * 0.14, {
      homeX: ARENA_W * 0.86,
      homeY: ARENA_H * 0.14,
      life: 12,
      laserExitSide: true,
    });
  });
  b.at(39, (ctx) => {
    ctx.enemy(EN_LASER, -40, ARENA_H * 0.82, {
      homeX: ARENA_W * 0.14,
      homeY: ARENA_H * 0.82,
      life: 12,
      laserExitSide: true,
    });
  });
  b.at(39.5, (ctx) => {
    ctx.enemy(EN_LASER, ARENA_W + 40, ARENA_H * 0.82, {
      homeX: ARENA_W * 0.86,
      homeY: ARENA_H * 0.82,
      life: 12,
      laserExitSide: true,
    });
  });
  // The guests: two chickens, then two ninjas, then two charging bullies
  // right at the end of the eyeball watch — the beasts carry straight into
  // the wall rush that follows.
  b.at(41, (ctx) => {
    ctx.enemy(EN_HEN, -30, ARENA_H * 0.5, {});
  });
  b.at(43, (ctx) => {
    ctx.enemy(EN_HEN, ARENA_W + 30, ARENA_H * 0.6, {});
  });
  b.at(44.5, (ctx) => {
    ctx.enemy(EN_NINJA, ARENA_W * 0.4, ARENA_H * 0.4, {});
  });
  b.at(46.5, (ctx) => {
    ctx.enemy(EN_NINJA, ARENA_W * 0.6, ARENA_H * 0.45, {});
  });
  b.at(49, (ctx) => {
    ctx.enemy(EN_BEAST, ARENA_W * 0.5, ARENA_H + 16, { phase: 1, life: 8 }); // BELOW, up the middle
  });
  b.at(51, (ctx) => {
    ctx.enemy(EN_BEAST, -16, ARENA_H * 0.5, { phase: 0, life: 8 }); // LEFT, across
  });

  // --- 4. WALL RUSH (54-72s): the pink walls come back in force, with
  // charging beasts threading between them and one more ninja. ---
  b.cue(54, "intensity:3");
  b.every(54, 70, 1.7, (ctx, t) => {
    const k = Math.round((t - 54) / 1.7);
    const axis = k % 2 === 0 ? "h" : "v";
    sweepBar(ctx, axis, 0.12 + ((k * 0.29) % 0.76), 1 + (k % 2), k % 2 === 1);
  });
  b.at(56, (ctx) => {
    ctx.enemy(EN_BEAST, ARENA_W + 16, ARENA_H * 0.45, { phase: 0, life: 9 }); // RIGHT
  });
  b.at(62, (ctx) => {
    ctx.enemy(EN_BEAST, ARENA_W * 0.5, -16, { phase: 1, life: 9 }); // TOP
  });
  b.at(60, (ctx) => {
    ctx.enemy(EN_NINJA, ARENA_W * 0.6, ARENA_H * 0.4, {});
  });
  b.at(58, (ctx) => gapWall(ctx, "h", 0.35, 260, 40, 190, true));
  b.at(66, (ctx) => gapWall(ctx, "h", 0.65, 260, 40, 190, true));

  // --- 5. GRAND FINALE (74-96s): everything at once. Two bombers post up on
  // both flanks, a laser eye holds dead center, two beasts charge through,
  // hens hop in, a last ninja dashes, and the pillars keep flying. ---
  b.at(74, (ctx) => {
    ctx.enemy(EN_BOMBER, -40, ARENA_H * 0.55, { homeX: ARENA_W * 0.3 });
  });
  b.at(77, (ctx) => {
    ctx.enemy(EN_BOMBER, ARENA_W + 40, ARENA_H * 0.65, { homeX: ARENA_W * 0.7 });
  });
  b.at(75, (ctx) => {
    ctx.enemy(EN_LASER, ARENA_W * 0.5, -40, {
      homeX: ARENA_W * 0.5,
      homeY: ARENA_H * 0.45,
      life: 14,
      laserExitSide: true,
    });
  });
  b.at(80, (ctx) => {
    ctx.enemy(EN_BEAST, -16, ARENA_H * 0.4, { phase: 0, life: 9 }); // LEFT
  });
  b.at(84, (ctx) => {
    ctx.enemy(EN_BEAST, ARENA_W * 0.5, ARENA_H + 16, { phase: 1, life: 9 }); // BELOW
  });
  b.at(79, (ctx) => {
    ctx.enemy(EN_HEN, ARENA_W + 30, ARENA_H * 0.75, {});
  });
  b.at(88, (ctx) => {
    ctx.enemy(EN_HEN, -30, ARENA_H * 0.7, {});
  });
  b.at(86, (ctx) => {
    ctx.enemy(EN_NINJA, ARENA_W * 0.65, ARENA_H * 0.38, {});
  });
  b.every(76, 94, 1.8, (ctx, t) => {
    const k = Math.round((t - 76) / 1.8);
    const axis = k % 2 === 0 ? "h" : "v";
    sweepBar(ctx, axis, 0.12 + ((k * 0.31) % 0.76), 1 + (k % 2), k % 2 === 0);
  });

  // --- Ramp down (97-104s): the gauntlet drains before the dragon's door ---
  b.cue(97.5, "phase:clear");
  return b.build();
}
